import { QueryObserverResult, useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { PatientDemographicSection } from '../constants/data'
import { queryClient } from '../store/provider/TanStackProvider'
import { DemographyMockProps } from '../types/demography.type'
import { EncounterDataMock, EncounterResMock } from '../types/encounter.type'
import { GeneralResMock } from '../types/general.type'
import {
  FamilyDataMock,
  HistoryResMock,
  MedDataMock,
  MedicalIncluded,
  SocialDataMock,
  SurgeryDataMock
} from '../types/history.type'
import { ImagingOrderIncludedResource } from '../types/imaging_order.type'
import { LabOrderIncludedResource } from '../types/lab.type'
import {
  PatientMedicationDataMock,
  PatientMedicationIncluded,
  PatientMedicationMeta,
  PatientMedicationsResponse,
  statusType
} from '../types/medication.type'
import {
  PatientIncluded,
  PatientMeta,
  PatientOptions,
  PatientPrescription,
  PatientPrescriptionIncluded,
  PatientPrescriptionResponse,
  PatientPrescriptionsResponse,
  PatientProfile,
  PatientSAttrProps
} from '../types/patient.types'
import { PatientDashboardResMock } from '../types/patientDashboard.type'
import { VitalDataMock, VitalResMock } from '../types/vital.type'
import { PatientImagingOrderResponse } from './patientImagingOrder.type'
import { PatientLabOrderResponse } from './patientLabOrder.type'
// import { PatientLabOrderResponse } from '../types/patientLabOrder.type'

export type PatientMedicationQuery = {
  query?: string
  status?: (keyof typeof statusType)[]
}

export type PatientPrescriptionDownloadStatus = 'available' | 'completed'

export type PatientPrescriptionQuery = {
  id?: string
  query?: string
  status?: PatientPrescriptionDownloadStatus | ''
}

export type UsePatientMedicationResult = {
  includedData?: PatientMedicationIncluded[]
  medicationIsLoading: boolean
  medications?: PatientMedicationDataMock[]
  pagination?: PatientMedicationMeta['pagination']
  patientMedications?: PatientMedicationsResponse
  refetchPatientMedications: () => Promise<QueryObserverResult<PatientMedicationsResponse, Error>>
}

export type UsePatientAllMedicationsResult = UsePatientMedicationResult

export type UsePatientEncountersResult = {
  encounters?: EncounterDataMock[]
  encountersIncluded?: EncounterResMock<EncounterDataMock[]>['included']
  encountersIsLoading: boolean
  patientEncounters?: EncounterResMock<EncounterDataMock[]>
}

export type UsePatientMedicalHistoriesResult = {
  included?: MedicalIncluded[]
  isLoading: boolean
  medicals?: MedDataMock[]
}

export type UsePatientFamilyHistoriesResult = {
  families?: FamilyDataMock[]
  isLoading: boolean
}

export type UsePatientSurgicalHistoriesResult = {
  isLoading: boolean
  surgeries?: SurgeryDataMock[]
}

export type UsePatientSocialHistoriesResult = {
  isLoading: boolean
  socials?: SocialDataMock[]
}

export type UsePatientPrescriptionsResult = {
  patientPrescriptionIncluded?: PatientPrescriptionIncluded[]
  patientPrescriptions?: PatientPrescription[]
  patientPrescriptionsIsLoading: boolean
  patientPrescriptionsPagination?: PatientPrescriptionsResponse['meta']['pagination']
  refetchPatientPrescriptions: () => Promise<QueryObserverResult<PatientPrescriptionsResponse, Error>>
}

export type UsePatientPrescriptionResult = {
  fetchPatientPrescription: (id: string) => Promise<PatientPrescriptionResponse>
  patientPrescriptionIsLoading: boolean
}

export type UseRecordPatientPrescriptionDownloadResult = {
  recordPrescriptionDownload: (id: string) => Promise<Record<string, unknown>>
  recordPrescriptionDownloadIsLoading: boolean
}

export const usePatient = (arg?: PatientOptions) => {
  const id = arg?.id
  const query = arg?.query
  const page = arg?.page
  const pageCount = arg?.limit
  const ref = arg?.ref
  const phone_number = arg?.phone_number
  const f_name = arg?.f_name
  const l_name = arg?.l_name
  const gender = arg?.gender
  const life_status = arg?.life_status
  const address = arg?.address
  const date_of_birth = arg?.date_of_birth
  const encounter_type = arg?.encounter_type
  const sort_dir = arg?.sort_dir || 'desc'
  const sort_by = arg?.sort_by || 'created_at'
  const refetchInterval = arg?.refetchInterval

  const { data: patients, isLoading: patientIsLoading } = useQuery({
    refetchInterval,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<PatientSAttrProps[], PatientIncluded[], PatientMeta>>(
        '/patients',
        {
          params: {
            ...(query && { query }),
            ...(page && { 'page[number]': page }),
            ...(pageCount && { 'page[size]': pageCount }),
            ...(sort_dir && { sort_dir }),
            ...(sort_by && { sort_by }),
            ...(address && { address }),
            ...(ref && { ref }),
            ...(phone_number && { phone_number }),
            ...(f_name && { f_name }),
            ...(l_name && { l_name }),
            ...(gender && { gender }),
            ...(life_status && { life_status }),
            ...(encounter_type && { encounter_type }),
            ...(date_of_birth && { date_of_birth }),
            stat: true
          }
        }
      )
      return data
    },
    queryKey: [
      'patients',
      {
        address,
        date_of_birth,
        f_name,
        gender,
        l_name,
        life_status,
        page,
        pageCount,
        phone_number,
        query,
        ref,
        encounter_type
      }
    ]
  })

  const { data: singlePatient, isLoading: singlePatientIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<PatientSAttrProps, PatientIncluded[]>>(`patients/${id}`)
      return data
    },
    queryKey: ['patients', { id }]
  })

  const { mutateAsync: createPatient } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post<GeneralResMock<PatientSAttrProps>>('/patients', formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoice'] })
    }
  })

  const { mutateAsync: updatePatient, isPending: isUpdatingPatient } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/patients/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['patients', { id }] })
    }
  })

  const { mutateAsync: deletePatient } = useMutation({
    mutationFn: async (id: string) => {
      const deleteEndpoint = `patients/${id}`
      const { data } = await AXIOS_INSTANCE.delete(deleteEndpoint)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
    }
  })
  return {
    createPatient,
    deletePatient,
    included: patients?.included,
    patientIsLoading,
    patients: patients?.data,
    patientMeta: patients?.meta.stats,
    patientsPagination: patients?.meta?.pagination,
    singleIncluded: singlePatient?.included,
    singlePatient: singlePatient?.data,
    singlePatientIsLoading,
    updatePatient,
    isUpdatingPatient
  }
}

export const usePatientDashboard = () => {
  const { data: patientDashboard, isLoading: patientDashboardIsLoading } = useQuery({
    enabled: true,
    queryFn: async () => {
      const endpoint = `patient/dashboard`
      const { data } = await AXIOS_INSTANCE.get<PatientDashboardResMock>(endpoint)
      return data
    },
    queryKey: ['patient_dashboard']
  })

  return {
    patientDashboard,
    patientDashboardIsLoading
  }
}

export const usePatientMedication = (arg?: PatientMedicationQuery): UsePatientMedicationResult => {
  const query = arg?.query
  const status = arg?.status ? arg.status : []
  const statusParam = status[0]

  const {
    data: patientMedications,
    isLoading: patientMedicationIsLoading,
    refetch: refetchPatientMedications
  } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<PatientMedicationsResponse>('/patient/medications', {
        params: {
          ...(statusParam && { status: statusParam }),
          ...(query && { query })
        }
      })
      return data
    },
    queryKey: ['patient_medications', { query, status: statusParam }]
  })

  return {
    includedData: patientMedications?.included,
    medicationIsLoading: patientMedicationIsLoading,
    medications: patientMedications?.data,
    pagination: patientMedications?.meta?.pagination,
    patientMedications,
    refetchPatientMedications
  }
}

export const usePatientAllMedications = (arg?: PatientMedicationQuery): UsePatientAllMedicationsResult => {
  const query = arg?.query
  const status = arg?.status ? arg.status : []
  const statusParam = status[0]

  const {
    data: patientMedications,
    isLoading: patientMedicationIsLoading,
    refetch: refetchPatientMedications
  } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<PatientMedicationsResponse>('/patient/all_medications', {
        params: {
          ...(statusParam && { status: statusParam }),
          ...(query && { query })
        }
      })
      return data
    },
    queryKey: ['patient_all_medications', { query, status: statusParam }]
  })

  return {
    includedData: patientMedications?.included,
    medicationIsLoading: patientMedicationIsLoading,
    medications: patientMedications?.data,
    pagination: patientMedications?.meta?.pagination,
    patientMedications,
    refetchPatientMedications
  }
}

export const usePatientEncounters = (): UsePatientEncountersResult => {
  const { data: patientEncounters, isLoading: encountersIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<EncounterResMock<EncounterDataMock[]>>('/patient/encounters')
      return data
    },
    queryKey: ['patient_encounters']
  })

  return {
    encounters: patientEncounters?.data,
    encountersIncluded: patientEncounters?.included,
    encountersIsLoading,
    patientEncounters
  }
}

export const usePatientMedicalHistories = (): UsePatientMedicalHistoriesResult => {
  const { data: medicals, isLoading } = useQuery({
    queryFn: async () => {
      const { data } =
        await AXIOS_INSTANCE.get<GeneralResMock<MedDataMock[], MedicalIncluded[]>>('/patient/medical_histories')
      return data
    },
    queryKey: ['patient_medical_histories']
  })

  return { included: medicals?.included, isLoading, medicals: medicals?.data }
}

export const usePatientFamilyHistories = (): UsePatientFamilyHistoriesResult => {
  const { data: families, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<HistoryResMock<FamilyDataMock[]>>('/patient/family_histories')
      return data
    },
    queryKey: ['patient_family_histories']
  })

  return { families: families?.data, isLoading }
}

export const usePatientSurgicalHistories = (): UsePatientSurgicalHistoriesResult => {
  const { data: surgeries, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<HistoryResMock<SurgeryDataMock[]>>('/patient/surgical_histories')
      return data
    },
    queryKey: ['patient_surgical_histories']
  })

  return { isLoading, surgeries: surgeries?.data }
}

export const usePatientSocialHistories = (): UsePatientSocialHistoriesResult => {
  const { data: socials, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<HistoryResMock<SocialDataMock[]>>('/patient/social_histories')
      return data
    },
    queryKey: ['patient_social_histories']
  })

  return { isLoading, socials: socials?.data }
}

export type UsePatientDemographyResult = {
  sections?: PatientDemographicSection[]
  isLoading: boolean
}

/** The signed-in patient's name and date of birth (patient portal), from the demography record. */
export const usePatientProfile = (): { patientProfile?: PatientProfile; patientProfileIsLoading: boolean } => {
  const { data: demography, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<DemographyMockProps>('/patient/demography')
      return data
    },
    queryKey: ['patient_demography']
  })
  const record = demography ? (Array.isArray(demography.data) ? demography.data[0] : demography.data) : undefined
  const attr = record?.attributes as (Record<string, unknown> & PatientProfile) | undefined
  const patientProfile: PatientProfile | undefined = attr
    ? {
        date_of_birth: attr.date_of_birth,
        first_name: attr.first_name,
        gender: attr.gender,
        last_name: attr.last_name,
        middle_name: attr.middle_name,
        ref: attr.ref
      }
    : undefined
  return { patientProfile, patientProfileIsLoading: isLoading }
}

export const usePatientDemography = (): UsePatientDemographyResult => {
  const { data: demography, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<DemographyMockProps>('/patient/demography')
      return data
    },
    queryKey: ['patient_demography']
  })

  let sections: PatientDemographicSection[] | undefined

  const record = demography ? (Array.isArray(demography.data) ? demography.data[0] : demography.data) : undefined

  if (record) {
    const attr = record.attributes as any
    const fmt = (obj: Record<string, string> | undefined) =>
      Object.entries(obj || {}).map(([k, v]) => ({
        label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        value: String(v ?? '')
      }))

    const personalItems = [
      { label: 'Title', value: attr.title || '' },
      { label: 'First Name', value: attr.first_name || '' },
      { label: 'Last Name', value: attr.last_name || '' },
      { label: 'Middle Name', value: attr.middle_name || '' },
      { label: 'DOB', value: attr.date_of_birth || '' },
      { label: 'Sex', value: attr.gender || '' },
      { label: 'License/ID', value: attr.identity_number || attr.unique_id || '' },
      { label: 'Marital Status', value: attr.marital_status || '' }
    ]

    const employmentItems = fmt(attr.employer)

    const guardianObj = attr.guardian_contact || attr.guardian || undefined
    const emergencyItems = fmt(guardianObj)

    const miscItems = fmt(attr.misc)
    const choicesItems = fmt(attr.choices)

    // try to extract email from included user if present
    const userIncluded = demography?.included ? (demography.included as any[]).find(i => i?.type === 'user') : undefined
    const email = userIncluded?.attributes?.email
    const contactItems: { label: string; value: string }[] = []
    if (attr.phone_number) contactItems.push({ label: 'Phone Number', value: attr.phone_number })
    if (email) contactItems.push({ label: 'Email', value: email })

    sections = [
      { title: 'Personal Details', items: personalItems },
      { title: 'Employment', items: employmentItems },
      { title: 'Emergency Contact', items: emergencyItems },
      { title: 'MISC', items: miscItems },
      { title: 'Choices', items: choicesItems },
      { title: 'Contact Details', items: contactItems }
    ]
  } else {
    sections = undefined
  }

  return { sections, isLoading }
}

export const usePatientPrescriptions = (arg?: PatientPrescriptionQuery): UsePatientPrescriptionsResult => {
  const query = arg?.query
  const status = arg?.status

  const {
    data: patientPrescriptions,
    isLoading: patientPrescriptionsIsLoading,
    refetch: refetchPatientPrescriptions
  } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<PatientPrescriptionsResponse>('/patient/prescriptions', {
        params: {
          ...(status && { status }),
          ...(query && { query })
        }
      })
      return data
    },
    queryKey: ['patient_prescriptions', { query, status }]
  })

  return {
    patientPrescriptionIncluded: patientPrescriptions?.included,
    patientPrescriptions: patientPrescriptions?.data,
    patientPrescriptionsIsLoading,
    patientPrescriptionsPagination: patientPrescriptions?.meta?.pagination,
    refetchPatientPrescriptions
  }
}

export const usePatientPrescription = (): UsePatientPrescriptionResult => {
  const { isPending: patientPrescriptionIsLoading, mutateAsync: fetchPatientPrescription } = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await AXIOS_INSTANCE.get<PatientPrescriptionResponse>(`/patient/prescriptions/${id}`)
      return data
    }
  })

  return {
    fetchPatientPrescription,
    patientPrescriptionIsLoading
  }
}

export const useRecordPatientPrescriptionDownload = (): UseRecordPatientPrescriptionDownloadResult => {
  const { isPending: recordPrescriptionDownloadIsLoading, mutateAsync: recordPrescriptionDownload } = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await AXIOS_INSTANCE.post<Record<string, unknown>>(
        `/patient/prescriptions/${id}/record_download`
      )
      return data
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['patient_prescriptions'] })
      queryClient.invalidateQueries({ queryKey: ['patient_prescription', { id }] })
    }
  })

  return {
    recordPrescriptionDownload,
    recordPrescriptionDownloadIsLoading
  }
}

export type UsePatientLabResultsArgs = {
  patientId?: string
  encounterId?: string
  query?: string
  limit?: string
  page?: string
  sortDir?: string
  sortBy?: string
}

export const usePatientLabOrder = (arg?: UsePatientLabResultsArgs) => {
  const patientId = arg?.patientId
  const encounterId = arg?.encounterId
  const query = arg?.query
  const limit = arg?.limit
  const page = arg?.page
  const sortDir = arg?.sortDir
  const sortBy = arg?.sortBy

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      let endpoint = `/patient/lab_orders?`
      if (patientId) endpoint += `patient_id=${patientId}&`
      if (encounterId) endpoint += `encounter_id=${encounterId}&`
      if (query) endpoint += `query=${encodeURIComponent(query)}&`
      if (limit) endpoint += `limit=${limit}&`
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (sortDir) endpoint += `sort_dir=${sortDir}&`
      if (sortBy) endpoint += `sort_by=${sortBy}&`
      endpoint = endpoint.replace(/[&?]$/, '')
      const { data } = await AXIOS_INSTANCE.get<PatientLabOrderResponse>(endpoint)
      return data
    },
    queryKey: ['patient_lab_orders', { patientId, encounterId, query, limit, page, sortDir, sortBy }]
  })

  return {
    labOrders: data,
    included: data?.included as LabOrderIncludedResource[] | undefined,
    isLoading
  }
}

export type UsePatientImagingOrdersArgs = {
  encounterId?: string
  lockedDate?: string
  studyNames?: string[]
  query?: string
  limit?: string
  page?: string
}

/** `GET /patient/imaging_orders` — the patient's own completed imaging orders (with results). */
export const usePatientImagingOrders = (arg?: UsePatientImagingOrdersArgs) => {
  const encounterId = arg?.encounterId
  const lockedDate = arg?.lockedDate
  const studyNames = arg?.studyNames
  const query = arg?.query
  const limit = arg?.limit
  const page = arg?.page

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      let endpoint = `/patient/imaging_orders?`
      if (encounterId) endpoint += `encounter_id=${encounterId}&`
      if (lockedDate) endpoint += `locked_date=${lockedDate}&`
      studyNames?.forEach(name => {
        endpoint += `study_names[]=${encodeURIComponent(name)}&`
      })
      if (query) endpoint += `query=${encodeURIComponent(query)}&`
      if (limit) endpoint += `limit=${limit}&`
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      endpoint = endpoint.replace(/[&?]$/, '')
      const { data } = await AXIOS_INSTANCE.get<PatientImagingOrderResponse>(endpoint)
      return data
    },
    queryKey: ['patient_imaging_orders', { encounterId, limit, lockedDate, page, query, studyNames }]
  })

  return {
    imagingOrders: data,
    included: data?.included as ImagingOrderIncludedResource[] | undefined,
    isLoading
  }
}

export type UsePatientVitalSignsArgs = {
  query?: string
  limit?: string
  page?: string
  sortDir?: string
  includeHistory?: boolean
}

/** `GET /patient/vital_signs` — the logged-in patient's own vital sign readings. */
export const usePatientVitalSigns = (arg?: UsePatientVitalSignsArgs) => {
  const query = arg?.query
  const limit = arg?.limit
  const page = arg?.page
  const sortDir = arg?.sortDir
  const includeHistory = arg?.includeHistory

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      let endpoint = `/patient/vital_signs?`
      if (query) endpoint += `query=${encodeURIComponent(query)}&`
      if (limit) endpoint += `limit=${limit}&`
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (sortDir) endpoint += `sort_dir=${sortDir}&`
      if (includeHistory) endpoint += `include_history=true&`
      endpoint = endpoint.replace(/[&?]$/, '')
      const { data } = await AXIOS_INSTANCE.get<VitalResMock<VitalDataMock[]>>(endpoint)
      return data
    },
    queryKey: ['patient_vital_signs', { includeHistory, limit, page, query, sortDir }]
  })

  return {
    vitals: data?.data || [],
    isLoading
  }
}
